/**
 * JulietteSidebarPresence — l'ancrage permanent de Juliette dans la sidebar.
 *
 * Présence vivante (orbe qui respire) logée dans le FOOTER fixe de la sidebar,
 * juste au-dessus du profil → toujours visible (petit écran, tous sous-menus
 * ouverts). Ligne horizontale compacte : orbe 44px à gauche + "Juliette" + une
 * ligne de statut à droite (gabarit d'un item de nav). Clic = ouvre Juliette.
 * Composant présentationnel pur (props) ; le câblage des hooks est dans
 * AppSidebar.
 *
 * États : couleur ambre + compteur dès qu'il y a des signaux en attente (PARTOUT,
 * dashboard compris) / phrase « whisper » du signal le plus urgent UNIQUEMENT sur
 * les écrans proactifs (CRM, Messagerie — pas de doublon avec le briefing du
 * dashboard) / flash "fait" (orbe verte) / repos. Sidebar réduite (`compact`) :
 * petite orbe sans texte.
 */

import { JulietteOrb } from "./JulietteOrb";

interface JulietteSidebarPresenceProps {
  /** Phrase de statut courte ("Je veille", "Analyste · prête"…). */
  statusLabel: string;
  /** Une session Juliette est en attente (point de notification sur l'orbe). */
  hasPending?: boolean;
  /**
   * Nombre de signaux en attente — pilote la COULEUR ambre + le badge compteur,
   * sur tous les écrans (la couleur réagit dès le dashboard).
   */
  signalCount?: number;
  /**
   * Phrase du signal le plus urgent, affichée SOUS « Juliette » uniquement sur
   * les écrans proactifs. `null`/absent = pas de phrase (juste couleur + badge).
   */
  whisper?: { headline: string } | null;
  /**
   * Flash bref « fait » (vert) : l'agent vient de tout traiter. Prioritaire sur
   * le whisper (qui retombe à 0 au même moment).
   */
  done?: boolean;
  /** Sidebar réduite en mode icône : orbe seule, sans texte. */
  compact?: boolean;
  /** Ouvre la surface de conversation Juliette. */
  onOpen: () => void;
}

export function JulietteSidebarPresence({
  statusLabel,
  hasPending = false,
  signalCount = 0,
  whisper = null,
  done = false,
  compact = false,
  onOpen,
}: JulietteSidebarPresenceProps) {
  // Couleur + badge : dès qu'il y a des signaux (partout). Phrase : seulement si
  // un whisper est fourni (écrans proactifs).
  const active = !done && signalCount > 0;
  const showHeadline = active && !!whisper;
  const orbState = done ? "done" : active ? "alert" : "idle";

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={active ? `Juliette — ${signalCount} à traiter` : "Ouvrir Juliette"}
      className={
        compact
          ? "flex w-full items-center justify-center rounded-xl px-2 py-2 transition-colors hover:bg-white/5"
          : "flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition-colors hover:bg-white/5"
      }
    >
      <span className="relative inline-flex shrink-0">
        <JulietteOrb
          size={44}
          state={orbState}
          hasPendingSession={hasPending}
          asAvatar
          glow
          ariaLabel="Juliette"
        />
        {active && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-warning px-1 text-2xs font-bold leading-none text-white ring-2 ring-sidebar">
            {signalCount}
          </span>
        )}
      </span>

      {!compact && (
        <span className="min-w-0 flex-1">
          <span className="block font-juliette text-sm font-semibold leading-tight text-sidebar-foreground">
            Juliette
          </span>
          {done ? (
            <span
              aria-live="polite"
              className="mt-0.5 block text-2xs font-medium text-success animate-in fade-in slide-in-from-bottom-1 duration-300"
            >
              Tout est traité
            </span>
          ) : showHeadline ? (
            <span
              aria-live="polite"
              className="mt-0.5 block text-2xs leading-snug text-warning line-clamp-1 animate-in fade-in slide-in-from-bottom-1 duration-300"
            >
              {whisper!.headline}
            </span>
          ) : (
            <span className="mt-0.5 block truncate text-2xs text-muted-foreground">
              {statusLabel}
            </span>
          )}
        </span>
      )}
    </button>
  );
}
